import { Component, OnInit } from '@angular/core';
import { PopupService } from '../modal/popup.service';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../alert/alert.service';
import { JhiEventManager } from '../core-utils';
import { finalize } from 'rxjs/internal/operators/finalize';
import { SubscriptionsManager } from '../util/subscriptions-manager';
import { TrialEnvironmentalLocation } from './model/trial-environmental-location.model';
import { QueryService } from './service/query.service';

@Component({
    selector: 'jhi-step-specify-and-weight-the-environments-location-filter',
    templateUrl: './location-filter.component.html'
})
export class LocationFilterComponent implements OnInit {
    selectedTraits: number[];
    environmentIds: number[];
    locationFilter: number[];
    subscriptionManager = new SubscriptionsManager();

    locations: TrialEnvironmentalLocation[];
    selectedItems: Map<number, TrialEnvironmentalLocation>;
    flattenedLocations: TrialEnvironmentalLocation[] = [];
    allSelected = false;
    isLoading: boolean;
    textFilter = '';
    traitFilterPlaceHolder = '';

    constructor(
        private queryService: QueryService,
        private translateService: TranslateService,
        private alertService: AlertService,
        private modal: NgbActiveModal,
        private eventManager: JhiEventManager
    ) {
        this.selectedItems = new Map<number, TrialEnvironmentalLocation>();

    }

    get visibleLocations(): TrialEnvironmentalLocation[] {
        if (this.isEmpty(this.textFilter)) {
            return this.flattenedLocations.filter(loc => this.isVisible(loc));
        }

        const matchedIds = new Set<number>();
        const filter = this.textFilter.trim().toLowerCase();

        for (const loc of this.flattenedLocations) {
            if (loc.locationName.toLowerCase().includes(filter)) {
                this.collectWithAncestors(loc, matchedIds);
            }
        }

        for (const loc of this.flattenedLocations) {
            loc.collapsed = !matchedIds.has(loc.locationId!) && loc.locations != null && loc.locations.length > 0;
        }

        return this.flattenedLocations.filter(loc =>
            this.isVisible(loc) && matchedIds.has(loc.locationId!)
        );
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.subscriptionManager.add = this.queryService.getTraitLocations(this.selectedTraits, this.environmentIds).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe((res: TrialEnvironmentalLocation[]) => this.onSuccess(res));
        this.traitFilterPlaceHolder = this.translateService.instant('multi-trait-query.panel.specify.and.weight.the.environments.environment.filter.location.place.holder');
    }

    getRowClass(location: TrialEnvironmentalLocation): string {
        if (!this.visibleLocations) {
            return '';
        }
        const visibleIndex = this.visibleLocations.indexOf(location);
        return visibleIndex % 2 === 0 ? 'odd-row' : 'even-row';
    }

    onLocationClick(event: any, location: TrialEnvironmentalLocation) {
        const checked = event.currentTarget.checked;
        location.selected = checked;

        if (location.locations) {
            const locations = Object.assign([], location.locations);
            while (locations.length) {
                const descendant: TrialEnvironmentalLocation = locations.pop();
                descendant.selected = checked;
                if (descendant.locations && descendant.locations.length > 0) {
                    locations.push.apply(locations, descendant.locations);
                }
            }
        }
        this.updateParentSelection(location);
    }

    updateParentSelection(location: TrialEnvironmentalLocation): void {
        let parent = location.parent;

        while (parent) {
            const children = Array.isArray(parent.locations) ? parent.locations : [];
            parent.selected = children.length > 0 && children.every(child => child.selected);
            parent = parent.parent;
        }
    }

    isEmpty(text?: string): boolean {
        return !text || text.trim() === '';
    }

    markSelectedNodes(locations: TrialEnvironmentalLocation[], selectedIds: number[]): boolean {
        let anyChildSelected = false;

        for (const loc of locations) {
            const isLeaf = !loc.locations || loc.locations.length === 0;

            if (isLeaf) {
                loc.selected = selectedIds.includes(loc.locationId!);
                if (loc.selected) {
                    anyChildSelected = true;
                }
            } else {
                const childrenHaveSelection = this.markSelectedNodes(loc.locations, selectedIds);
                if (childrenHaveSelection) {
                    const allChildrenSelected = loc.locations.every(child => child.selected);
                    loc.selected = allChildrenSelected;
                    loc.collapsed = allChildrenSelected;

                    anyChildSelected = true;
                } else {
                    loc.selected = false;
                    loc.collapsed = true;
                }
            }
        }

        return anyChildSelected;
    }

    dismiss() {
        this.modal.dismiss();
    }

    toggleCollapse(location: TrialEnvironmentalLocation) {
        location.collapsed = !location.collapsed;
    }

    toggleAll(event: any) {
        for (let loc of this.visibleLocations) {
            loc.selected = event.target.checked;
        }
    }

    flattenLocations(locations: TrialEnvironmentalLocation[], level = 0, parent?: TrialEnvironmentalLocation): TrialEnvironmentalLocation[] {
        let flat: TrialEnvironmentalLocation[] = [];

        for (let loc of locations) {
            loc.level = level;
            loc.parent = parent;
            flat.push(loc);
            if (loc.locations && loc.locations.length > 0) {
                flat.push(...this.flattenLocations(loc.locations, level + 1, loc));
            }
        }
        return flat;
    }

    isVisible(location: TrialEnvironmentalLocation): boolean {
        let current = location.parent;
        while (current) {
            if (current.collapsed) {
                return false;
            }
            current = current.parent;
        }
        return true;
    }

    hasLocationSelected() {
        return this.flattenedLocations.some(loc => loc.selected);
    }

    apply(): void {
        this.modal.close();
        this.locationFilter = this.flattenedLocations
            .filter(loc => this.isLeaf(loc) && this.isEffectivelySelected(loc))
            .map(loc => loc.locationId!)
            .filter(id => id !== undefined && id !== null);
        const content = {
            'locationFilter': this.locationFilter
        };
        this.eventManager.broadcast({ name: 'environmentFilterViewChanged', content });
    }

    reset() {
        this.modal.close();
        this.locationFilter = [];
        const content = {
            'locationFilter': this.locationFilter
        };
        this.eventManager.broadcast({ name: 'environmentFilterViewChanged', content });
    }

    countSelectedLocations(locations: TrialEnvironmentalLocation[]): number {
        let count = 0;

        if (!Array.isArray(locations) || locations.length === 0) {
            return count;
        }

        for (const loc of locations) {
            const children = loc.locations;
            const isLeaf = !Array.isArray(children) || children.length === 0;

            if (loc.selected && isLeaf) {
                count++;
            }

            count += this.countSelectedLocations(children);
        }

        return count;
    }

    countLeafLocations(locations: TrialEnvironmentalLocation[]): number {
        let count = 0;

        if (!Array.isArray(locations)) {
            return count;
        }

        for (const loc of locations) {
            const children = loc.locations;

            if (!Array.isArray(children) || children.length === 0) {
                count++;
            }
            count += this.countLeafLocations(children);
        }

        return count;
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    private collectWithAncestors(loc: TrialEnvironmentalLocation, matchedIds: Set<number>): void {
        matchedIds.add(loc.locationId!);
        let parent = loc.parent;
        while (parent) {
            matchedIds.add(parent.locationId!);
            parent.collapsed = false;
            parent = parent.parent;
        }
    }

    private onSuccess(data: TrialEnvironmentalLocation[]) {
        this.locations = data;
        this.markSelectedNodes(this.locations, this.locationFilter);
        this.flattenedLocations = this.flattenLocations(this.locations);
    }

    private isLeaf(loc: TrialEnvironmentalLocation): boolean {
        return !loc.locations || loc.locations.length === 0;
    }

    private isEffectivelySelected(loc: TrialEnvironmentalLocation): boolean {
        if (loc.selected) {
            return true;
        }

        let parent = loc.parent;
        while (parent) {
            if (parent.selected) {
                return true;
            }
            parent = parent.parent;
        }

        return false;
    }
}

@Component({
    selector: 'jhi-specify-and-weight-environment-location-filter-popup',
    template: ''
})
export class LocationFilterPopupComponent implements OnInit {
    routeSub: any;
    selectedTraits: number[];
    environmentIds: number[];
    locationFilter: number[];

    constructor(
        private route: ActivatedRoute,
        private popupService: PopupService
    ) {
        const queryParams = this.route.snapshot.queryParams;
        this.selectedTraits = this.convertToArray(queryParams.selectedTraits);
        this.environmentIds = this.convertToArray(queryParams.environmentIds);
        this.locationFilter = this.convertToArray(queryParams.locationFilter);
    }

    ngOnDestroy(): void {
        if (this.routeSub) {
            this.routeSub.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.routeSub = this.route.params.subscribe(() => {
            const modal = this.popupService.open(LocationFilterComponent as Component, {
                modalDialogClass: 'modal-lg',
                backdrop: 'static'
            });
            modal.then((modalRef) => {
                modalRef.componentInstance.selectedTraits = this.selectedTraits;
                modalRef.componentInstance.environmentIds = this.environmentIds;
                modalRef.componentInstance.locationFilter = this.locationFilter;
            });
        });
    }

    private convertToArray(param): number[] {
        if (!param) {
            return [];
        }

        const numbersArray = Array.isArray(param) ? param : [param];
        return  numbersArray.map(n => Number(n));
    }

}
